import { MISSING_FIELD_LABELS, OPTIONAL_FIELDS } from '@sitzmark/core';
import type { MissingField } from '@sitzmark/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNotice } from '../../components/notice.js';
import { Button, Callout, Loading, Modal, Mono } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { askedFields, buildStylePatch, buildVariantPatches, orderQueue } from './finish-run.js';
import { BrandPicker } from './brand-picker.js';
import { Field, Picker } from './style-settings.js';
import './products.css';

/**
 * The guided finish-this-product pass (revision item 39).
 *
 * An import creates styles from what the vendor file knows and leaves empty
 * what it cannot: description, retail where MSRP was missing, the gender
 * filing, bins, targets. This walks that debt one style at a time, asking
 * **only for what is missing** — the measure of success is an evening, so it
 * must never ask for anything twice or anything the file already answered.
 *
 * *Skip* is always there and Escape always leaves; the pass is a helper,
 * never a gate. What is asked is recomputed from the freshly-loaded style,
 * not the queue snapshot — someone may have finished half of it since.
 */

type Unfinished = Awaited<ReturnType<typeof api.products.unfinished.query>>;
type QueueEntry = Unfinished['styles'][number];

const EMPTY_ANSWERS = {
  departmentId: '',
  brandId: '',
  season: '',
  description: '',
  retail: {} as Record<string, string>,
  bin: '',
  target: '',
};

/** How many styles the banner names, with what each still owes. */
const NAMED = 3;

/**
 * The purchase-order end of the same pass (item 39: "reachable from the PO
 * too"): the moment a delivery is put away is the moment the gaps are in
 * hand, so the order's row says how many of its styles still owe something
 * and opens the pass scoped to exactly those.
 */
export function FinishFromOrder({ purchaseOrderId }: { purchaseOrderId: string }) {
  const [open, setOpen] = useState(false);
  const debt = useQuery({
    queryKey: ['products', 'unfinished', 'count', purchaseOrderId],
    queryFn: () => api.products.unfinished.query({ purchaseOrderId, limit: NAMED }),
    staleTime: 60_000,
  });

  if ((debt.data?.total ?? 0) === 0) return null;
  const total = debt.data!.total;
  const named = debt.data!.styles.slice(0, NAMED);

  return (
    <>
      <Callout tone="info">
        <div className="finish-banner">
          <span>
            {total} model{total === 1 ? '' : 's'} from this order still need
            {total === 1 ? 's' : ''} finishing.
            {/*
              What is still owed, said where it is seen (2026-10-02): saving a
              style with one answer left blank kept this banner up, and nothing
              said which answer — a style is finished only when every gap is.
            */}
            {named.map((style) => (
              <span key={style.id} className="finish-banner__owed">
                {style.name} still needs{' '}
                {style.missing.map((field) => MISSING_FIELD_LABELS[field]).join(', ')}.
              </span>
            ))}
            {total > named.length ? (
              <span className="finish-banner__owed">…and {total - named.length} more.</span>
            ) : null}
          </span>
          <span className="finish-banner__actions">
            <Button variant="ghost" onClick={() => setOpen(true)}>
              Finish them
            </Button>
          </span>
        </div>
      </Callout>
      {open ? (
        <FinishStylesDialog purchaseOrderId={purchaseOrderId} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

/**
 * Styles skipped in this visit to the page, so the next pass asks them last.
 * The page's memory, not the store's: a reload starts the order over.
 */
const skippedThisVisit = new Set<string>();

/** What the pass is narrowed to; nothing set means every unfinished style. */
interface FinishScope {
  readonly missing?: MissingField;
  readonly vendorId?: string;
  readonly departmentId?: string;
  readonly term: string;
}

/** The things a style can owe, in the order the pass asks them. */
const OWED_FIELDS: readonly MissingField[] = ['category', 'brand', 'season', 'retail', 'targets'];

/** The strip that narrows the pass: what is owed, whose, where, or by name. */
function FinishScopeBar({
  scope,
  term,
  total,
  byMissing,
  onScope,
  onTerm,
}: {
  scope: FinishScope;
  term: string;
  total: number | undefined;
  byMissing: Partial<Record<MissingField, number>> | undefined;
  onScope: (next: FinishScope) => void;
  onTerm: (next: string) => void;
}) {
  const vendors = useQuery({
    queryKey: ['purchasing', 'vendors', 'all'],
    queryFn: () => api.purchasing.vendors.query(),
    staleTime: 300_000,
  });
  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    staleTime: 300_000,
  });
  return (
    <div className="finish-scope" role="group" aria-label="Which models to finish">
      <label className="finish-scope__field">
        <span className="label-micro">Still needs</span>
        <select
          aria-label="Still needs"
          className="select"
          value={scope.missing ?? ''}
          onChange={(event) =>
            onScope({
              ...scope,
              missing: event.target.value === '' ? undefined : (event.target.value as MissingField),
            })
          }
        >
          <option value="">Anything</option>
          {OWED_FIELDS.map((field) => (
            <option key={field} value={field}>
              {MISSING_FIELD_LABELS[field]}
              {byMissing?.[field] === undefined ? '' : ` (${byMissing[field]})`}
            </option>
          ))}
        </select>
      </label>
      <label className="finish-scope__field">
        <span className="label-micro">Vendor</span>
        <select
          aria-label="Vendor"
          className="select"
          value={scope.vendorId ?? ''}
          onChange={(event) =>
            onScope({
              ...scope,
              vendorId: event.target.value === '' ? undefined : event.target.value,
            })
          }
        >
          <option value="">All</option>
          {(vendors.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </label>
      <label className="finish-scope__field">
        <span className="label-micro">Category</span>
        <select
          aria-label="Category"
          className="select"
          value={scope.departmentId ?? ''}
          onChange={(event) =>
            onScope({
              ...scope,
              departmentId: event.target.value === '' ? undefined : event.target.value,
            })
          }
        >
          <option value="">All</option>
          {(taxonomy.data?.departments ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.path}
            </option>
          ))}
        </select>
      </label>
      <label className="finish-scope__field">
        <span className="label-micro">Model</span>
        <input
          className="input"
          placeholder="Name or model #"
          value={term}
          onChange={(event) => onTerm(event.target.value)}
        />
      </label>
      <span className="finish-scope__count text-secondary" role="status">
        {total === undefined ? '' : `${total} model${total === 1 ? '' : 's'}`}
      </span>
    </div>
  );
}

export function FinishStylesDialog({
  purchaseOrderId,
  onClose,
}: {
  /** Set when opened from a purchase order: only what that order carried. */
  purchaseOrderId?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();

  /*
   * The queue is captured once, then owned locally. Refetching `unfinished`
   * after every save would reshuffle the tail under the operator's feet —
   * finishing the head must not renumber "style 3 of 18" into a different
   * style 3.
   */
  const [queue, setQueue] = useState<QueueEntry[] | null>(null);
  const [index, setIndex] = useState(0);
  /** Styles answers were saved for — some finished, some still owing a field. */
  const [finished, setFinished] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  /** Left part-way: show the tally rather than vanish (FB-1073). */
  const [stopped, setStopped] = useState(false);
  const [answers, setAnswers] = useState(EMPTY_ANSWERS);

  /*
   * Which styles the pass works through (2026-10-05, a tester: "no visible way
   * to search or filter by what is missing, vendor, order, department" — with
   * four thousand styles owed). Changing it starts the queue again.
   */
  const [scope, setScope] = useState<FinishScope>({ term: '' });
  /** Each batch of up to 200 is a round; keeping going asks for the next. */
  const [round, setRound] = useState(0);
  const [scopeTerm, setScopeTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () =>
        setScope((current) =>
          current.term === scopeTerm.trim() ? current : { ...current, term: scopeTerm.trim() },
        ),
      300,
    );
    return () => clearTimeout(timer);
  }, [scopeTerm]);
  useEffect(() => {
    setQueue(null);
    setIndex(0);
  }, [scope]);

  const unfinished = useQuery({
    queryKey: ['products', 'unfinished', 'queue', purchaseOrderId ?? '', scope, round],
    queryFn: () =>
      api.products.unfinished.query({
        limit: 200,
        ...(purchaseOrderId === undefined ? {} : { purchaseOrderId }),
        ...(scope.missing === undefined ? {} : { missing: scope.missing }),
        ...(scope.vendorId === undefined ? {} : { vendorId: scope.vendorId }),
        ...(scope.departmentId === undefined ? {} : { departmentId: scope.departmentId }),
        ...(scope.term === '' ? {} : { term: scope.term }),
      }),
    // Fresh every time the pass opens. It was kept for the whole visit, so a
    // reopened pass replayed the list from before the last one's work.
    staleTime: 0,
    gcTime: 0,
  });
  useEffect(() => {
    if (unfinished.data !== undefined && queue === null) {
      setQueue(orderQueue(unfinished.data.styles, skippedThisVisit));
    }
  }, [unfinished.data, queue]);

  /** What is still owed after this pass — the banner's own count. */
  const remaining = useQuery({
    queryKey: ['products', 'unfinished', 'count'],
    queryFn: () => api.products.unfinished.query({ limit: 1 }),
  });

  const current = queue?.[index];

  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    staleTime: 300_000,
  });

  const detail = useQuery({
    queryKey: ['products', 'get', current?.id ?? ''],
    queryFn: () => api.products.get.query({ productId: current!.id }),
    enabled: current !== undefined,
  });

  // A new style is a new set of questions; carrying answers across would
  // write one style's season onto the next.
  useEffect(() => setAnswers(EMPTY_ANSWERS), [current?.id]);

  const advance = () => {
    setIndex((step) => step + 1);
    void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    void queryClient.invalidateQueries({ queryKey: ['products', 'unfinished', 'count'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (detail.data === undefined || current === undefined) return;
      const asked = askedFields(detail.data.product, detail.data.variants);

      const stylePatch = buildStylePatch(asked, answers);
      if (stylePatch !== null) {
        await api.products.update.mutate({ productId: current.id, ...stylePatch });
      }

      const { patches, problem } = buildVariantPatches(asked, detail.data.variants, answers);
      if (problem !== undefined) throw new Error(problem);
      if (patches.length > 0) {
        await api.products.updateVariants.mutate({ productId: current.id, patches });
      }

      return stylePatch !== null || patches.length > 0;
    },
    onSuccess: (wrote) => {
      recordBreadcrumb('products', 'finished a model');
      if (wrote === true) {
        setFinished((count) => count + 1);
        if (detail.data !== undefined) notify({ title: `Saved — ${detail.data.product.name}` });
      }
      if (current !== undefined) skippedThisVisit.delete(current.id);
      void queryClient.invalidateQueries({ queryKey: ['products', 'get', current?.id ?? ''] });
      advance();
    },
  });

  // ---- the three end states: loading, empty, done --------------------------

  if (queue === null) {
    return (
      <Modal open title="Finish these models" onClose={onClose}>
        {unfinished.isError ? (
          <Callout tone="danger">{errorMessage(unfinished.error)}</Callout>
        ) : (
          <Loading label="Looking for unfinished models" />
        )}
      </Modal>
    );
  }

  const scopeBar =
    purchaseOrderId === undefined ? (
      <FinishScopeBar
        scope={scope}
        term={scopeTerm}
        total={unfinished.data?.total}
        byMissing={unfinished.data?.byMissing}
        onScope={setScope}
        onTerm={setScopeTerm}
      />
    ) : null;
  const narrowed =
    scope.missing !== undefined ||
    scope.vendorId !== undefined ||
    scope.departmentId !== undefined ||
    scope.term !== '';

  if (queue.length === 0 && narrowed) {
    return (
      <Modal
        open
        wide
        title="No model matches"
        onClose={onClose}
        primaryAction={
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        }
      >
        {scopeBar}
        <p className="text-secondary">
          Nothing unfinished matches what is chosen above. Change it, or choose Anything and All.
        </p>
      </Modal>
    );
  }

  if (queue.length === 0) {
    return (
      <Modal
        open
        title="Nothing needs finishing"
        onClose={onClose}
        primaryAction={
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        }
      >
        <p className="text-secondary">
          {purchaseOrderId === undefined
            ? 'Every model has its filing, its prices, its bins and its targets.'
            : 'Everything this order carried is already finished.'}
        </p>
      </Modal>
    );
  }

  if (current === undefined || stopped) {
    const left = remaining.data?.total;
    // The batch is done and more are owed: the next batch, without reopening.
    const more = current === undefined && !stopped && (unfinished.data?.total ?? 0) > queue.length;
    return (
      <Modal
        open
        title={current === undefined ? "That's the pass done" : 'Done for now'}
        onClose={onClose}
        secondaryAction={
          more ? (
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          ) : undefined
        }
        primaryAction={
          more ? (
            <Button
              variant="primary"
              onClick={() => {
                setQueue(null);
                setIndex(0);
                setRound((count) => count + 1);
              }}
            >
              Keep going
            </Button>
          ) : (
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          )
        }
      >
        <p>
          Answers saved on {finished} model{finished === 1 ? '' : 's'}
          {skippedCount > 0 ? `, ${skippedCount} skipped` : ''}.
          {left === undefined
            ? ''
            : ` ${left} model${left === 1 ? '' : 's'} still need${left === 1 ? 's' : ''} something.`}
        </p>
        <p className="text-secondary">
          A model counts as finished only when everything it was missing is filled in — anything
          left blank stays owed. Skipped models come last the next time you open the pass.
        </p>
      </Modal>
    );
  }

  // ---- one style, only its gaps --------------------------------------------

  const asked =
    detail.data === undefined ? [] : askedFields(detail.data.product, detail.data.variants);
  const unpriced =
    detail.data === undefined
      ? []
      : detail.data.variants.filter((entry) => entry.active && entry.retailCents === 0);
  // Filed at any level, read as a path (decision 214).
  const categoryOptions = (taxonomy.data?.departments ?? []).map((category) => ({
    value: category.id,
    label: category.path,
  }));

  return (
    <Modal
      open
      wide
      title={`Finish ${current.name}`}
      description={
        <>
          Model {index + 1} of {Math.max(unfinished.data?.total ?? 0, queue.length)} ·{' '}
          <Mono>{current.styleNumber}</Mono>
          {current.vendor !== null ? ` · ${current.vendor}` : ''} — only what is missing is asked.
        </>
      }
      // Leaving part-way says what was done instead of simply vanishing.
      onClose={() => (finished > 0 || skippedCount > 0 ? setStopped(true) : onClose())}
      secondaryAction={
        <Button
          variant="ghost"
          onClick={() => {
            skippedThisVisit.add(current.id);
            setSkippedCount((count) => count + 1);
            advance();
          }}
        >
          Skip
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={detail.data === undefined ? 'Loading the model' : undefined}
          onClick={() => save.mutate()}
        >
          Save &amp; next
        </Button>
      }
    >
      {scopeBar}
      {detail.isError ? <Callout tone="danger">{errorMessage(detail.error)}</Callout> : null}
      {detail.isPending ? <Loading label="Loading the model" /> : null}
      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}

      {detail.data !== undefined && asked.length === 0 ? (
        <Callout tone="success">
          Already finished — someone got here first. Save &amp; next moves on.
        </Callout>
      ) : null}

      {detail.data !== undefined && asked.length > 0 ? (
        <div className="finish-pass">
          {asked.includes('category') ? (
            <Picker
              label="Category"
              searchable
              value={answers.departmentId}
              onChange={(value) => setAnswers({ ...answers, departmentId: value })}
              options={categoryOptions}
            />
          ) : null}
          {asked.includes('brand') ? (
            <BrandPicker
              value={answers.brandId}
              onChange={(value) => setAnswers({ ...answers, brandId: value })}
              brands={taxonomy.data?.brands ?? []}
              vendorName={current.vendor}
            />
          ) : null}
          {asked.includes('season') ? (
            <Field
              label="Season"
              value={answers.season}
              placeholder="FA26"
              onChange={(value) => setAnswers({ ...answers, season: value })}
            />
          ) : null}
          {asked.includes('description') ? (
            <div className="field">
              <label className="field__label" htmlFor="finish-description">
                Description
              </label>
              <textarea
                id="finish-description"
                className="input textarea"
                rows={2}
                value={answers.description}
                onChange={(event) => setAnswers({ ...answers, description: event.target.value })}
              />
            </div>
          ) : null}

          {asked.includes('retail') ? (
            <div className="finish-pass__retail">
              <div className="finish-pass__retail-head">
                <span className="field__label">
                  Retail — {unpriced.length} size{unpriced.length === 1 ? '' : 's'} with no price
                </span>
                {unpriced.some((entry) => entry.msrpCents !== null) ? (
                  <button
                    type="button"
                    className="chip"
                    onClick={() =>
                      setAnswers({
                        ...answers,
                        retail: Object.fromEntries(
                          unpriced
                            .filter((entry) => entry.msrpCents !== null)
                            .map((entry) => [entry.id, (entry.msrpCents! / 100).toFixed(2)]),
                        ),
                      })
                    }
                  >
                    Use MSRP
                  </button>
                ) : null}
              </div>
              {unpriced.map((entry) => (
                <div key={entry.id} className="finish-pass__price-row">
                  <Mono>{entry.sku}</Mono>
                  <span>
                    {[entry.colorName, entry.sizeLabel, entry.altSize].filter(Boolean).join(' · ')}
                  </span>
                  <span className="text-secondary">
                    {entry.msrpCents === null ? 'no MSRP' : `MSRP ${money(entry.msrpCents)}`}
                  </span>
                  <input
                    className="price-input money"
                    inputMode="decimal"
                    aria-label={`Retail price for ${entry.sku}`}
                    value={answers.retail[entry.id] ?? ''}
                    onChange={(event) =>
                      setAnswers({
                        ...answers,
                        retail: { ...answers.retail, [entry.id]: event.target.value },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          ) : null}

          {asked.includes('bin') ? (
            <Field
              label="Bin"
              value={answers.bin}
              placeholder="A-3"
              hint="Applied to every size that has no bin yet; sizes already binned keep theirs."
              onChange={(value) => setAnswers({ ...answers, bin: value })}
            />
          ) : null}
          {asked.includes('targets') ? (
            <Field
              label="Target per size"
              value={answers.target}
              placeholder="4"
              hint="What a full rack looks like; open to buy and a reorder work up to it. One number fills every size missing one — refine a size later from its Edit."
              onChange={(value) => setAnswers({ ...answers, target: value })}
            />
          ) : null}

          <p className="text-secondary finish-pass__missing">
            {(() => {
              const owed = asked.filter((field) => !OPTIONAL_FIELDS.includes(field));
              const optional = asked.filter((field) => OPTIONAL_FIELDS.includes(field));
              return (
                <>
                  {owed.length > 0
                    ? `Needed to finish: ${owed.map((field) => MISSING_FIELD_LABELS[field]).join(', ')}. `
                    : 'Nothing more is needed to finish. '}
                  {optional.length > 0
                    ? `Optional: ${optional.map((field) => MISSING_FIELD_LABELS[field]).join(', ')} — leave blank if there is none. `
                    : ''}
                  Save &amp; next writes only what you answered.
                </>
              );
            })()}
          </p>
        </div>
      ) : null}
    </Modal>
  );
}
